import {
  Box,
  Button,
  Flex,
  Popover as MantinePopover,
  Modal,
  type FloatingPosition,
  type PopoverProps as MantinePopoverProps
} from "@mantine/core";
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { CHARACTER_LIMIT, isTooLongText } from "../../../utils/isTooLongText";
import { truncateToStringElipsis } from "../../../utils/truncateToStringElipsis";
import { MarkdownLink } from "../../ReactMarkdown/MarkdownLink";
import "./Popover.scss";

type PopoverProps = CustomPopoverProps & SimplifiedMantinePopoverProps;

type SimplifiedMantinePopoverProps = Pick<
  MantinePopoverProps,
  "opened" | "position"
>;
type CustomPopoverProps = {
  children: React.ReactNode;
  text: string;
  ref?: React.Ref<HTMLDivElement>;
  onOpenModal?: () => void;
};

const POPOVER_MAX_CHARACTERS = CHARACTER_LIMIT + 300;

function Popover({
  children,
  opened,
  position = "left",
  text,
  onOpenModal,
  ref
}: PopoverProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const openModal = () => {
    if (typeof onOpenModal !== "undefined") {
      onOpenModal();
    }
    toggleModal();
  };

  const shouldShowTruncatedTextInPopover = text.length > POPOVER_MAX_CHARACTERS;

  const modalBodyText = shouldShowTruncatedTextInPopover
    ? truncateToStringElipsis(text)
    : text;

  const isBodyTooLong = isTooLongText(text, POPOVER_MAX_CHARACTERS);

  function preventClicksBelowPopover(e: React.MouseEvent) {
    e.stopPropagation();
  }

  return (
    <>
      <MantinePopover
        opened={opened}
        position={position}
        withArrow
        zIndex={1070}
      >
        <MantinePopover.Target>{children}</MantinePopover.Target>
        <MantinePopover.Dropdown
          className="popover-dropdown"
          onClick={preventClicksBelowPopover}
        >
          <div ref={ref}>
            <ReactMarkdown
              components={{
                a: MarkdownLink
              }}
            >
              {modalBodyText}
            </ReactMarkdown>
            {isBodyTooLong && (
              <Flex justify="flex-end" mt="md">
                <Button size="xs" onClick={openModal}>
                  Vollständige Erklärung
                </Button>
              </Flex>
            )}
          </div>
        </MantinePopover.Dropdown>
      </MantinePopover>

      <Modal
        autoFocus
        className="icon-help-text-modal"
        opened={isModalOpen}
        size="xl"
        withCloseButton={false}
        onClose={toggleModal}
      >
        <Box mt="md">
          <ReactMarkdown
            components={{
              a: MarkdownLink
            }}
          >
            {text}
          </ReactMarkdown>
        </Box>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Schließen
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export { Popover, FloatingPosition as PopoverPosition, PopoverProps };
